
import type Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import debounce from 'lodash/debounce';
import { mapState, mapActions } from 'vuex';
import { defineComponent } from '@nuxtjs/composition-api';
import BravadoTextInputV3 from '@/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import { baseUrl, redirectTo } from '@/components/utils/browser/url';
import { getPortfolioPath } from '@/utils/routes';
import { slug } from '~/components/utils/validate/rules';
import CopyIcon from '~/assets/svg/copy.vue.svg';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import PublicPortfolioEmailSignature from '~/components/partials/public-portfolio/PublicPortfolioEmailSignature/PublicPortfolioEmailSignature.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import BravadoToggle from '~/components/common/BravadoToggle/BravadoToggle.vue';

export default defineComponent({
  name: 'PublicPortfolioShareModal',
  components: {
    PublicPortfolioEmailSignature,
    PublicPortfolioModal,
    CopyIcon,
    BravadoCallout,
    BravadoToggle,
    BravadoTextInputV3,
  },
  props: {
    userSlug: {
      type: String,
      required: true,
    },
  },
  validations: {
    slug: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(100),
      slug,
      async isSlugUnique(value): Promise<boolean> {
        // @ts-ignore
        this.checkingSlug = true;

        const checkSlugResponse = await this.$api.$get(`/profile/check_slug.json?slug=${value}`);

        // @ts-ignore
        this.checkingSlug = false;
        return checkSlugResponse.is_valid;
      },
    },
  },
  computed: {
    ...mapState('portfolio', ['configuration']),
    publicPortfolioPrefix() {
      return `${baseUrl()}/p/`;
    },
    slugError(): string {
      const userSlug = this.$v.slug;

      if (!userSlug.$dirty) return '';
      if (!userSlug.$invalid) return '';

      if (!userSlug.required) return 'URL is required';
      if (!userSlug.minLength) return 'URL must be at least 3 characters long';
      if (!userSlug.maxLength) return 'URL must be at most 100 characters long';
      if (!userSlug.slug) return "Please only use letters, numbers and '-' character";
      if (!userSlug.isSlugUnique && !this.checkingSlug) return 'This URL has already been taken';

      return '';
    },
    slugErrorHint(): string {
      return (
        this.slugError ||
        'Note: Your custom URL must contain 3-100 letters or numbers. Please do not use spaces, symbols, or special characters.'
      );
    },
    profilePath() {
      return getPortfolioPath(this.slug);
    },
  },
  methods: {
    ...mapActions('portfolio', ['updatePortfolioConfig']),
    updateUser() {
      if (this.$v.slug.$invalid) return;

      this.$emit('upsert', { userAttrs: { slug: this.slug } });
    },
    async copyProfileUrl() {
      const url = `${this.publicPortfolioPrefix}${this.slug}`;
      await navigator.clipboard.writeText(url);
      this.$analytics.logEvent('public-portfolio__share-portfolio', {
        to: 'link',
      });
      this.$notice.success('Profile link copied');
    },
    copyEmailSignature() {
      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNode((this.$refs.signature as Vue).$el);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        const isSuccess = document.execCommand('copy');
        selection.removeAllRanges();

        if (isSuccess) {
          this.$notice.success('Email signature copied');
        } else {
          this.$notice.error('Could not copy email signature');
        }
      }

      this.$analytics.logEvent('public-portfolio__share-portfolio', {
        to: 'email-signature',
      });
    },
    async onPublicStatusChange(value: boolean): Promise<void> {
      this.$analytics.logEvent('public-portfolio__visible-toggle__click', { shared: value });

      const configurationAttrs = {
        isPublic: value,
      };

      try {
        // @ts-ignore
        await this.updatePortfolioConfig({ configuration: { is_public: value } });
        this.$emit('upsert', { configurationAttrs });
      } catch (e) {
        this.$notice.error('Something went wrong.', e);
        this.$sentryUtils.error(e);
      }
    },
    handleClose() {
      if (this.$v.slug.$invalid) return;
      if (this.checkingSlug) return;

      if (!this.publicUrlHasChnaged) {
        this.$router.push({
          query: { dynamic_modal: undefined },
        });
        return;
      }
      redirectTo(this.profilePath);
    },

    showPreview() {
      this.$modal.hide(this.$constants.dynamicModals.PORTFOLIO_SHARE_MODAL);

      this.$router.push(`${this.profilePath}/preview`);
    },
    updatePublicUrl: debounce(function updateSlugDebounced(newSlug) {
      // @ts-expect-error
      if (!this.$v.slug.$dirty) {
        // @ts-expect-error
        this.$v.slug.$touch();
      }
      // @ts-expect-error
      this.publicUrlHasChnaged = true;
      // @ts-expect-error
      this.slug = newSlug;
    }, 300),
  },
  mixins: [validationMixin],
  data(): { slug: string; checkingSlug: boolean; publicUrlHasChnaged: boolean } {
    return {
      slug: this.userSlug,
      checkingSlug: false,
      publicUrlHasChnaged: false,
    };
  },
  watch: {
    // Watcher used due to Vuelidate bug with async validation
    '$v.slug.$pending'() {
      if (this.$v.slug.$invalid) return;
      if (!this.publicUrlHasChnaged) return;

      this.updateUser();
    },
  },
});
